import styled from '@emotion/native';
import {
  AuthEvent,
  type BaseAuthUser,
  type PasswordlessAuthClient,
  type PromiseOfOptional,
} from '@quromedical/auth/client';
import type { GroupSecurity } from '@quromedical/fhir-common';
import type { UserMeResponse, UserPreferenceWard } from '@quromedical/models';
import { registerForPushNotifications } from 'config/notifications';
import { Spinner, Text } from 'design-system';
import type { ExpoPushToken } from 'expo-notifications';
import { logger } from 'helpers';
import { analytics } from 'helpers/analytics';
import { useAsyncStorage } from 'hooks/useAsyncStorage';
import { UserPreferenceApi } from 'integration';
import type { Session } from 'integration/AuthApi';
import type React from 'react';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

const userApi = new UserPreferenceApi();

type UserProviderProps = PropsWithChildren<{
  authClient: AuthClient;
}>;

export interface UserSession {
  hasAny: (groups: GroupSecurity[]) => boolean;
  getProfile: () => UserMeResponse | undefined;
  getPermissions: () => GroupSecurity[];
  getUser: () => BaseAuthUser | undefined;
  getSession: () => PromiseOfOptional<Session>;
  getAuthToken: () => PromiseOfOptional<string>;
  logout: () => Promise<void>;
  selectWard: (wardId: string) => Promise<void>;
  ward: string | undefined;
  wards: UserPreferenceWard[];
  authClient?: AuthClient;
  getGroups: () => Promise<void>;
  user: BaseAuthUser | undefined;
}

export const UserContext = createContext<UserSession>({
  hasAny: () => false,
  getUser: () => undefined,
  getProfile: () => undefined,
  getPermissions: () => [],
  logout: () => Promise.resolve(undefined),
  getSession: () => Promise.resolve(undefined),
  getAuthToken: () => Promise.resolve(undefined),
  selectWard: () => Promise.resolve(undefined),
  ward: undefined,
  wards: [],
  authClient: undefined,
  getGroups: () => Promise.resolve(undefined),
  user: undefined,
});

const LoadingWrapper = styled.View({
  flexDirection: 'column',
  alignContent: 'center',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 1,
});

const Background = styled.View(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.color['base-grey'],
}));

export const UserProvider: React.FC<UserProviderProps> = ({
  children,
  authClient,
}) => {
  const [me, setMe] = useState<UserMeResponse>();
  const [ward, setWard] = useState<string>();
  const [user, setUser] = useState<BaseAuthUser>();
  const [isLoading, setLoading] = useState(true);
  const [pushToken, setPushToken] = useState<ExpoPushToken>();
  const [isLoggingOut, setLoggingOut] = useState(false);
  const [authSession, setAuthSession] = useState<Session>();

  const wards = useMemo(() => me?.wards || [], [me]);
  const uniqWards = useMemo(() => {
    return new Set(me?.wards.map(({ id }) => id) || []);
  }, [me]);

  const storage = useAsyncStorage(me?.id || 'me');

  useEffect(() => {
    storage.getItem().then((wardId) => {
      if (wardId && uniqWards.has(wardId)) {
        setWard(wardId);
      }
    });
  }, [storage, uniqWards]);

  const permissions = useMemo(() => me?.permissions || [], [me]);

  const getPermissions = useCallback(() => permissions, [permissions]);
  const getProfile = useCallback(() => me, [me]);

  const selectWard = useCallback(
    async (wardId: string) => {
      if (uniqWards.has(wardId)) {
        await storage.setItem(wardId);
        setWard(wardId);
      }
    },
    [uniqWards, storage],
  );

  const getUser = useCallback(() => user, [user]);

  const getGroupsAsync = useCallback(async () => {
    try {
      const userGroups = await userApi.getMe();
      setMe(userGroups);
    } catch (error) {
      logger.error(error);
    }
  }, []);

  const getSession = useCallback(async () => {
    setLoading(true);
    const currentSession = await authClient.getSession();
    if (currentSession) {
      setAuthSession(currentSession);
      await getGroupsAsync();
      const currentUser = currentSession.getUser();
      const registeredPushToken = await registerForPushNotifications();
      setPushToken(registeredPushToken);
      setUser(currentUser);

      if (registeredPushToken) {
        userApi.registerPushToken(registeredPushToken.data).catch(logger.error);
      }
    }
    setLoading(false);

    return currentSession;
  }, [authClient, getGroupsAsync]);

  const hasAny = useCallback(
    (query: GroupSecurity[]) => {
      const foundGroup = query.find((g) => permissions.includes(g));
      return !!foundGroup;
    },
    [permissions],
  );

  const logout = useCallback(async () => {
    try {
      setLoggingOut(true);
      setUser(undefined);
      await authClient.signOut();
    } catch (error) {
      logger.error(error);
    } finally {
      analytics.setUser(undefined);

      //clear the local storage to ensure no patient data and other sensitive data is left behind
      localStorage.clear();

      if (pushToken) {
        userApi.unregisterPushToken(pushToken.data).catch(logger.error);
      }
      setAuthSession(undefined);
      setLoggingOut(false);
    }
  }, [pushToken, authClient]);

  const getAuthToken = useCallback(async () => {
    if (!authSession) {
      return undefined;
    }

    return authSession.getIdToken();
  }, [authSession]);

  const value = useMemo<UserSession>(
    () => ({
      getProfile,
      getPermissions,
      hasAny,
      logout,
      getSession,
      getUser,
      getAuthToken,
      authClient,
      selectWard,
      ward,
      wards,
      user,
      getGroups: getGroupsAsync,
    }),
    [
      getPermissions,
      getProfile,
      hasAny,
      logout,
      getSession,
      getUser,
      authClient,
      getAuthToken,
      selectWard,
      ward,
      wards,
      getGroupsAsync,
      user,
    ],
  );

  useEffect(() => {
    const subscription = authClient.addEventListener(AuthEvent.SignOut, () =>
      setAuthSession(undefined),
    );
    return () => {
      subscription.remove();
    };
  }, [authClient]);

  useEffect(() => {
    getSession().catch(logger.error);
  }, [getSession]);

  useEffect(() => {
    if (!user) {
      return;
    }
    analytics.setUser(user.email);
  }, [user]);

  if (isLoading) {
    return (
      <Background>
        <LoadingWrapper>
          <Spinner />
          <Text>Logging in</Text>
        </LoadingWrapper>
      </Background>
    );
  }

  if (isLoggingOut) {
    return (
      <Background>
        <LoadingWrapper>
          <Spinner />
          <Text>Logging out</Text>
        </LoadingWrapper>
      </Background>
    );
  }

  return (
    <UserContext.Provider value={value}>
      <Background>{children}</Background>
    </UserContext.Provider>
  );
};

type AuthClient = PasswordlessAuthClient<BaseAuthUser>;
